//>>> SSE utility with refresh+reconnect behavior (works during idle)
import { ENV } from '@configs/env';
import { useAuthStore } from '@stores/auth.store';
import { refreshAuthSingleFlight, hardSessionExpiredHardReset } from '@utils/networkSession';

function buildSseUrl(path, queryParams = {}) {
    const url = new URL(`${ENV.API_URL}${path}`);
    Object.entries(queryParams || {}).forEach(([key, value]) => {
        if (value == null) return;
        url.searchParams.set(key, String(value));
    });
    return url.toString();
}

function parseSseBlock(block) {
    const lines = String(block || '')
        .split('\n')
        .map((l) => l.replace(/\r$/, ''));

    let eventType = 'message';
    const dataLines = [];

    for (const line of lines) {
        if (!line || line.startsWith(':')) continue;
        if (line.startsWith('event:')) {
            eventType = line.slice('event:'.length).trim() || eventType;
            continue;
        }
        if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).trimStart());
        }
    }

    if (!dataLines.length) return null;
    return { eventType, data: dataLines.join('\n') };
}

/**
 * Open SSE stream using fetch() so we can send Authorization header.
 * Returns an EventTarget-like object: addEventListener/removeEventListener/close.
 */
const getSseRetryMs = () => Number(ENV.SSE_RETRY_MS) || 3000;

const openSSE = (path, options = {}) => {
    const { queryParams = {} } = options;

    const target = new EventTarget();
    let closed = false;
    let abortController = null;
    let reconnectTimer = null;

    const connect = async () => {
        if (closed) return;
        if (typeof navigator !== 'undefined' && !navigator.onLine) return;
        const token = useAuthStore.getState().accessToken;
        if (!token) return;

        abortController = new AbortController();

        try {
            const response = await fetch(buildSseUrl(path, queryParams), {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'text/event-stream',
                },
                signal: abortController.signal,
            });

            if (!response.ok) {
                if (response.status === 401) {
                    try {
                        const refreshed = await refreshAuthSingleFlight();
                        if (!refreshed?.accessToken) {
                            hardSessionExpiredHardReset();
                            return;
                        }
                        return connect();
                    } catch (error) {
                        const status = error?.status ?? error?.response?.status;
                        const isAuth = status === 400 || status === 401 || status === 403;
                        if (isAuth) {
                            hardSessionExpiredHardReset();
                        } else {
                            console.warn('SSE stream refresh failed due to network/server error. Retrying connect later.', error?.message);
                            if (typeof navigator !== 'undefined' && navigator.onLine) {
                                reconnectTimer = setTimeout(connect, getSseRetryMs());
                            }
                        }
                        return;
                    }
                }
                // retry on transient server errors
                if (typeof navigator !== 'undefined' && navigator.onLine) {
                    reconnectTimer = setTimeout(connect, getSseRetryMs());
                }
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) {
                if (typeof navigator !== 'undefined' && navigator.onLine) {
                    reconnectTimer = setTimeout(connect, getSseRetryMs());
                }
                return;
            }

            const decoder = new TextDecoder();
            let buffer = '';

            while (!closed) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const blocks = buffer.split(/\n\n+/);
                buffer = blocks.pop() ?? '';
                for (const block of blocks) {
                    const parsed = parseSseBlock(block);
                    if (!parsed) continue;
                    const ev = new MessageEvent(parsed.eventType || 'message', { data: parsed.data });
                    target.dispatchEvent(ev);
                }
            }

            // Server idle/proxy timeouts often close the stream cleanly; keep listening.
            if (!closed && typeof navigator !== 'undefined' && navigator.onLine) {
                reconnectTimer = setTimeout(connect, getSseRetryMs());
            }
        } catch {
            if (!closed && typeof navigator !== 'undefined' && navigator.onLine) {
                reconnectTimer = setTimeout(connect, getSseRetryMs());
            }
        }
    };

    const onOnline = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = null;
        connect();
    };

    const onOffline = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = null;
        try {
            abortController?.abort();
        } catch {
            // ignore
        }
        abortController = null;
    };

    if (typeof window !== 'undefined') {
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
    }

    connect();

    target.close = () => {
        closed = true;
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        }
        try {
            abortController?.abort();
        } catch {
            // ignore
        }
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = null;
        abortController = null;
    };

    return target;
};

// Helper to create SSE with automatic React Query cache updates
const createSSEWithQueryCache = (path, queryClient, options = {}) => {
    const { queryKey, onCreated, onUpdated, onDeleted, onError } = options;

    const eventSource = openSSE(path, options);

    eventSource.addEventListener('created', (event) => {
        try {
            const data = JSON.parse(event.data);
            if (queryKey && queryClient) {
                queryClient.setQueryData(queryKey, (old) => {
                    if (Array.isArray(old)) {
                        return [data, ...old];
                    }
                    return data;
                });
            }
            if (onCreated) onCreated(data, event);
        } catch (error) {
            console.error('SSE created event error:', error);
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('updated', (event) => {
        try {
            const data = JSON.parse(event.data);
            if (queryKey && queryClient) {
                queryClient.setQueryData(queryKey, (old) => {
                    if (Array.isArray(old)) {
                        return old.map((item) => (item.id === data.id ? data : item));
                    }
                    return data;
                });
            }
            if (onUpdated) onUpdated(data, event);
        } catch (error) {
            console.error('SSE updated event error:', error);
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('deleted', (event) => {
        try {
            const data = JSON.parse(event.data);
            if (queryKey && queryClient) {
                queryClient.setQueryData(queryKey, (old) => {
                    if (Array.isArray(old)) {
                        return old.filter((item) => item.id !== data.id);
                    }
                    return null;
                });
            }
            if (onDeleted) onDeleted(data, event);
        } catch (error) {
            console.error('SSE deleted event error:', error);
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('error', (event) => {
        console.error('SSE error:', event);
        if (onError) onError(event);
    });

    return eventSource;
};

export { openSSE, createSSEWithQueryCache };
